import React, { useState } from 'react';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import EmptyState from './EmptyState';
import { classesAPI, usersAPI, attendanceAPI, feesAPI } from '../services/api';
import { useLanguage } from '../LanguageContext';

export default function AttendanceTable({ 
    attendanceData = [], 
    onStatusChange, 
    highlightedStudent, 
    onConfirmCash,
    onConfirmPayment,
    classInfo,
    currentDate,
    onScheduleMakeup
}) {
    const { t, language } = useLanguage();
    const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
    const [absentPromptStudent, setAbsentPromptStudent] = useState(null);
    const [makeupModalStudent, setMakeupModalStudent] = useState(null);
    const [teachersList, setTeachersList] = useState([]);
    const [isScheduling, setIsScheduling] = useState(false);
    const [isSavingCramSession, setIsSavingCramSession] = useState(false);
    const [showStudentRoster, setShowStudentRoster] = useState(false);
    const [confirmingCycleFor, setConfirmingCycleFor] = useState(null);
    const [cancellingMakeupFor, setCancellingMakeupFor] = useState(null);
    const [deletingAttendanceFor, setDeletingAttendanceFor] = useState(null);
    const [makeupForm, setMakeupForm] = useState({
        date: '',
        startTime: '17:00',
        endTime: '18:00',
        format: 'in_person',
        teacherId: '',
        notes: ''
    });

    const isCramClass = classInfo?.class_type === 'cram_school' || 
        Boolean(classInfo?.cram_school_name) ||
        (attendanceData && attendanceData.length > 0 && (attendanceData[0].student_type === 'cram_school' || attendanceData[0].class_type === 'cram_school'));

    const cramRate = parseFloat(classInfo?.hourly_wage || classInfo?.rate || attendanceData?.[0]?.class_hourly_wage || attendanceData?.[0]?.class_rate || 700);
    const cramSchoolName = classInfo?.cram_school_name || attendanceData?.[0]?.cram_school_name || attendanceData?.[0]?.class_cram_school_name || 'Cram School';

    // cramRate is per hour, not per session -- a 2hr class at $700/hr pays
    // $1400, not $700. Computed here from classInfo's start/end time so the
    // "secured" labels show the real session total instead of the bare
    // hourly figure (which previously read as a flat per-session amount).
    const cramDurationHours = (() => {
        if (!classInfo?.start_time || !classInfo?.end_time) return 1;
        const [sh, sm] = classInfo.start_time.split(':').map(Number);
        const [eh, em] = classInfo.end_time.split(':').map(Number);
        const hours = (eh * 60 + em - (sh * 60 + sm)) / 60;
        return hours > 0 ? hours : 1;
    })();
    const cramSessionTotal = Math.round(cramRate * cramDurationHours);

    const currentCramStatus = (() => {
        if (classInfo?.cram_session_status) return classInfo.cram_session_status;
        if (classInfo?.stats?.session_status) return classInfo.stats.session_status;
        if (attendanceData && attendanceData.length > 0) {
            const hasHeld = attendanceData.some(s => s.status === 'present' || s.status === 'late');
            const allAbsent = attendanceData.length > 0 && attendanceData.every(s => s.status === 'absent');
            if (hasHeld) return 'held';
            if (allAbsent) return 'not_held';
        }
        return 'pending';
    })();

    const handleRecordCramSession = async (status) => {
        if (!classInfo?.id) return;
        setIsSavingCramSession(true);
        try {
            await attendanceAPI.recordCramSession({
                class_id: classInfo.id,
                date: currentDate,
                status: status,
                notes: status === 'present' ? `Cram school session held at ${cramSchoolName}` : `Cram school session not held at ${cramSchoolName}`
            });

            if (attendanceData && attendanceData.length > 0 && onStatusChange) {
                attendanceData.forEach(s => {
                    if (s.student_id) onStatusChange(s.student_id, 'status', status);
                });
            }

            if (status === 'present') {
                toast.success(language === 'zh'
                    ? `🎉 課堂已記錄！已確保課堂薪資 $${cramSessionTotal} NTD ($${cramRate}/hr × ${cramDurationHours}hr)`
                    : `🎉 Class recorded! Secured $${cramSessionTotal} NTD ($${cramRate}/hr × ${cramDurationHours}hr)`);
            } else {
                toast.success(language === 'zh' ? '課堂已標記為未上課 / 取消' : 'Class marked as not held / cancelled');
            }

            if (onScheduleMakeup) onScheduleMakeup();
        } catch (err) {
            console.error("Failed to record cram session:", err);
            toast.error(err.response?.data?.error || (language === 'zh' ? '記錄課堂狀態失敗' : 'Failed to record session status'));
        } finally {
            setIsSavingCramSession(false);
        }
    };

    const handleResetCramSession = async () => {
        if (!classInfo?.id || !currentDate) return;
        const confirmMsg = language === 'zh' 
            ? '確定要重設此課堂狀態為「待確認」嗎？' 
            : 'Reset this class session status to Pending?';
        if (!window.confirm(confirmMsg)) return;
        setIsSavingCramSession(true);
        try {
            await attendanceAPI.cancelSession(classInfo.id, currentDate);
            if (attendanceData && attendanceData.length > 0 && onStatusChange) {
                attendanceData.forEach(s => {
                    if (s.student_id) onStatusChange(s.student_id, 'status', 'pending');
                });
            }
            toast.success(language === 'zh' ? '已重設課堂狀態為待確認' : 'Session status reset to pending');
            if (onScheduleMakeup) onScheduleMakeup();
        } catch (err) {
            console.error('Failed to reset session:', err);
            toast.error(language === 'zh' ? '重設失敗' : 'Reset failed');
        } finally {
            setIsSavingCramSession(false);
        }
    };

    if ((!attendanceData || attendanceData.length === 0) && !isCramClass) {
        return <EmptyState message="No students found for this class or filter." />;
    }

    const allSelected = attendanceData.length > 0 && selectedStudentIds.size === attendanceData.length;
    const selectedCount = selectedStudentIds.size;

    const handleToggleSelect = (studentId) => {
        setSelectedStudentIds(prev => {
            const next = new Set(prev);
            if (next.has(studentId)) {
                next.delete(studentId);
            } else {
                next.add(studentId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedStudentIds(new Set());
        } else {
            setSelectedStudentIds(new Set(attendanceData.map(s => s.student_id)));
        }
    };

    const handleSetStudentStatus = (student, newStatus) => {
        onStatusChange(student.student_id, 'status', newStatus);
        if (newStatus === 'absent' && !isCramClass && student.student_type !== 'cram_school') {
            setAbsentPromptStudent(student);
        }
    };

    const handleBatchSetStatus = (status) => {
        if (selectedStudentIds.size === 0) return;
        let absentSt = null;
        selectedStudentIds.forEach(id => {
            onStatusChange(id, 'status', status);
            if (status === 'absent' && !absentSt) {
                absentSt = attendanceData.find(s => s.student_id === id);
            }
        });
        setSelectedStudentIds(new Set());
        if (status === 'absent' && absentSt && !isCramClass && absentSt.student_type !== 'cram_school') {
            setAbsentPromptStudent(absentSt);
        }
    };

    const openMakeupModal = (student) => {
        const base = currentDate ? new Date(currentDate) : new Date();
        const nextWeek = new Date(base);
        nextWeek.setDate(nextWeek.getDate() + 7);
        const y = nextWeek.getFullYear();
        const m = String(nextWeek.getMonth() + 1).padStart(2, '0');
        const d = String(nextWeek.getDate()).padStart(2, '0');
        const defaultDateStr = `${y}-${m}-${d}`;

        const origStart = classInfo?.start_time ? classInfo.start_time.slice(0, 5) : '17:00';
        const origEnd = classInfo?.end_time ? classInfo.end_time.slice(0, 5) : '18:00';
        const origTeacher = classInfo?.teacher_id || student.teacher_id || '';

        setMakeupModalStudent(student);
        setMakeupForm({
            date: defaultDateStr,
            startTime: origStart,
            endTime: origEnd,
            format: classInfo?.format || 'in_person',
            teacherId: origTeacher,
            notes: language === 'zh'
                ? `補課 (學生 ${student.student_name} - ${classInfo?.name || '班級'}，原上課日 ${currentDate || ''})`
                : `Make-up for ${student.student_name} - ${classInfo?.name || 'Class'} on ${currentDate || ''}`
        });

        // Load teachers if not already loaded
        if (teachersList.length === 0) {
            usersAPI.getAll().then(users => {
                const teachers = users.filter(u => u.role === 'teacher' || u.role === 'admin');
                setTeachersList(teachers);
            }).catch(err => console.error("Failed to load teachers for makeup:", err));
        }
    };

    const handleScheduleMakeupSubmit = async (e) => {
        e.preventDefault();
        if (!makeupModalStudent) return;
        if (!makeupForm.date || !makeupForm.startTime || !makeupForm.endTime) {
            toast.error(language === 'zh' ? '請填寫補課日期及上下課時間。' : 'Please fill in make-up date and start/end time.');
            return;
        }

        setIsScheduling(true);
        try {
            await classesAPI.scheduleMakeup({
                student_id: makeupModalStudent.student_id,
                original_class_id: classInfo?.id || null,
                makeup_date: makeupForm.date,
                makeup_for_date: currentDate || null,
                start_time: makeupForm.startTime,
                end_time: makeupForm.endTime,
                teacher_id: makeupForm.teacherId || classInfo?.teacher_id || null,
                format: makeupForm.format,
                notes: makeupForm.notes
            });

            toast.success(language === 'zh'
                ? `🎉 已成功安排 ${makeupModalStudent.student_name} 於 ${makeupForm.date} 的單次補課！`
                : `🎉 Successfully scheduled make-up for ${makeupModalStudent.student_name} on ${makeupForm.date}!`);
            setMakeupModalStudent(null);
            if (onScheduleMakeup) {
                onScheduleMakeup();
            }
        } catch (err) {
            console.error('Failed to schedule makeup:', err);
            toast.error(err.response?.data?.error || (language === 'zh' ? '安排補課失敗，請重試。' : 'Failed to schedule make-up.'));
        } finally {
            setIsScheduling(false);
        }
    };

    // Cancels an already-scheduled make-up class entirely (including its own
    // attendance record, if it was already held) - frees up the "Schedule
    // Make-up" button for this absence again.
    const handleCancelMakeup = async (student) => {
        if (!student.existing_makeup_id) return;
        const confirmMsg = language === 'zh'
            ? '確定要取消這堂補課嗎？這將會移除該補課堂的所有出席紀錄。'
            : 'Cancel this make-up class? This will remove any attendance recorded for it.';
        if (!window.confirm(confirmMsg)) return;
        setCancellingMakeupFor(student.student_id);
        try {
            await classesAPI.cancelMakeup(student.existing_makeup_id);
            toast.success(language === 'zh' ? '補課已取消。' : 'Make-up class cancelled.');
            if (onScheduleMakeup) onScheduleMakeup();
        } catch (err) {
            toast.error(err.response?.data?.error || (language === 'zh' ? '取消補課失敗。' : 'Failed to cancel make-up.'));
        } finally {
            setCancellingMakeupFor(null);
        }
    };

    // Fully deletes an attendance record (as opposed to resetting its status
    // to 'pending', which leaves a row behind and won't reverse an already
    // confirmed payment) - for correcting a wrongly recorded entry.
    const handleDeleteAttendance = async (student) => {
        if (!student.attendance_id) return;
        const confirmMsg = language === 'zh'
            ? `確定要刪除 ${student.student_name} 這筆出席紀錄嗎？此動作無法復原。`
            : `Delete this attendance record for ${student.student_name}? This cannot be undone.`;
        if (!window.confirm(confirmMsg)) return;
        setDeletingAttendanceFor(student.student_id);
        try {
            await attendanceAPI.deleteRecord(student.attendance_id);
            toast.success(language === 'zh' ? '出席紀錄已刪除。' : 'Attendance record deleted.');
            if (onScheduleMakeup) onScheduleMakeup();
        } catch (err) {
            toast.error(err.response?.data?.error || (language === 'zh' ? '刪除失敗。' : 'Failed to delete record.'));
        } finally {
            setDeletingAttendanceFor(null);
        }
    };

    const handleMarkAllPresent = () => {
        attendanceData.forEach(s => {
            onStatusChange(s.student_id, 'status', 'present');
        });
        setSelectedStudentIds(new Set());
    };

    // Unified confirm handler supporting bank transfer and cash
    const handleConfirm = (studentIds, amount, name, method = 'bank_transfer') => {
        if (onConfirmPayment) {
            onConfirmPayment(studentIds, amount, name, method);
        } else if (onConfirmCash) {
            onConfirmCash(studentIds, amount, name);
        }
    };

    // Confirm an entire "per_4_sessions" billing block at once, rather than
    // per lesson - settles every held session since the last confirmed
    // payment in one action.
    const handleConfirmCycle = async (student, method = 'bank_transfer') => {
        setConfirmingCycleFor(student.student_id);
        try {
            const res = await feesAPI.confirmCycle({
                student_id: student.student_id,
                class_id: classInfo?.id,
                payment_method: method,
                notes: `${student.cycle_session_count}-session cycle confirmed by staff for ${student.student_name}`
            });
            toast.success(res.message || `Cycle confirmed for ${student.student_name}!`);
            if (onScheduleMakeup) onScheduleMakeup();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not confirm billing cycle.');
        } finally {
            setConfirmingCycleFor(null);
        }
    };

    // Dynamic attending count calculation:
    // Rates adapt dynamically to the number of students who actually attended (present or late).
    // E.g. in a 3-student tutoring class: 1 absent (2 present) -> 1v2 rate ($350); 2 absent (1 present) -> 1v1 rate ($650/600).
    const attendingCount = attendanceData.filter(s => 
        (s.status === 'present' || s.status === 'late') &&
        s.student_type !== 'cram_school' &&
        s.class_type !== 'cram_school'
    ).length;

    const hasRecordedAttendance = attendanceData.some(s => 
        s.status === 'present' || s.status === 'late' || s.status === 'absent'
    );

    const effectiveStudentCount = hasRecordedAttendance
        ? (attendingCount > 0 ? attendingCount : 1)
        : (attendanceData.filter(s => s.student_type !== 'cram_school' && s.class_type !== 'cram_school').length || 1);

    // Rate calculation helper:
    // - Absent students: 0 NTD (no fee / make-up credit)
    // - Settled/paid fees: preserves recorded student.fee_amount
    // - Custom rate: student.custom_rate
    // - Cram school student or class: uses cram school hourly wage or rate (default 700)
    // - Private tutoring: dynamic 1v1 ($650/600), 1v2 ($350), 1v3+ ($300) based on attending count
    const calculateFee = (student) => {
        if (student.status === 'absent') return 0;
        if (student.fee_status === 'paid' && student.fee_amount !== undefined && student.fee_amount !== null) {
            return parseFloat(student.fee_amount);
        }
        if (student.custom_rate !== null && student.custom_rate !== undefined && !isNaN(parseFloat(student.custom_rate)) && parseFloat(student.custom_rate) > 0) {
            return parseFloat(student.custom_rate);
        }

        const isCram = student.student_type === 'cram_school' || student.class_type === 'cram_school';
        if (isCram) {
            const cramRate = parseFloat(student.class_hourly_wage || student.class_rate || classInfo?.hourly_wage || classInfo?.rate || 700);
            return Math.round(cramRate / (attendanceData.length || 1));
        }

        if (effectiveStudentCount <= 1) {
            return student.class_format === 'online' ? 600 : 650;
        } else if (effectiveStudentCount === 2) {
            return 350;
        } else {
            return 300;
        }
    };

    const getRateDescription = (student) => {
        if (student.status === 'absent') {
            return t('att_absent_no_fee');
        }
        const isCram = student.student_type === 'cram_school' || student.class_type === 'cram_school';
        if (isCram) {
            const cramRate = parseFloat(student.class_hourly_wage || student.class_rate || classInfo?.hourly_wage || classInfo?.rate || 700);
            return language === 'zh'
                ? `安親補習班固定課鐘 ($${cramRate} NTD / 全班合約價)`
                : `Cram School Flat Rate ($${cramRate} NTD / Flat Contract)`;
        }
        if (student.custom_rate !== null && student.custom_rate !== undefined && !isNaN(parseFloat(student.custom_rate)) && parseFloat(student.custom_rate) > 0) {
            return `Custom Rate ($${parseFloat(student.custom_rate)} NTD)`;
        }

        if (effectiveStudentCount <= 1) {
            return student.class_format === 'online' ? '1v1 Rate ($600/hr Online)' : '1v1 Rate ($650/hr In-Person)';
        } else if (effectiveStudentCount === 2) {
            return '1v2 Rate ($350/hr per student)';
        } else {
            return `1v${effectiveStudentCount}+ Rate ($300/hr per student)`;
        }
    };

    // Grouping for Sibling Families and Cram Schools
    const groups = {};
    attendanceData.forEach(student => {
        const isCram = student.student_type === 'cram_school' || student.class_type === 'cram_school';
        let groupKey;
        let groupType = 'family';
        let groupName = '';

        if (isCram) {
            const cramName = student.cram_school_name || student.class_cram_school_name || 'Cram School';
            groupKey = `cram_${cramName.replace(/\s+/g, '_')}`;
            groupType = 'cram_school';
            groupName = language === 'zh' ? `${cramName} (補習班代結)` : `${cramName} (School Settlement)`;
        } else {
            groupKey = student.family_id 
                ? `fam_${student.family_id}` 
                : (student.parent_phone ? `phone_${student.parent_phone}` : null);
            groupName = student.family_name || `${student.parent_name || 'Sibling'} Family`;
        }

        if (groupKey) {
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    key: groupKey,
                    type: groupType,
                    name: groupName,
                    students: []
                };
            }
            groups[groupKey].students.push(student);
        }
    });

    const multiGroups = Object.values(groups).filter(g => g.students.length > 1);

    return (
        <div className="space-y-4">
            {/* Dedicated Cram School Session Control Card */}
            {isCramClass && (
                <div className="bg-gradient-to-r from-purple-50/90 via-indigo-50/50 to-white p-4 sm:p-5 rounded-2xl border border-purple-200 shadow-xs space-y-3.5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xl">🏫</span>
                                <h4 className="text-base font-extrabold text-purple-950">
                                    {cramSchoolName} {language === 'zh' ? '(安親補習班)' : '(Cram School)'}
                                </h4>
                                <span className="bg-purple-100 text-purple-900 border border-purple-300 text-xs px-2.5 py-0.5 rounded-full font-bold">
                                    ${cramRate} NTD/hr &times; {cramDurationHours}hr = ${cramSessionTotal} NTD
                                </span>
                            </div>
                            <p className="text-xs text-purple-800/80 mt-1">
                                💡 {t('att_cram_notice')}
                            </p>
                        </div>

                        {/* Status Badge */}
                        <div className="flex items-center gap-2">
                            {currentCramStatus === 'held' ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-100 border border-emerald-300 text-emerald-900 font-extrabold text-xs shadow-2xs">
                                    <span>✓</span>
                                    <span>{t('att_status_held')}</span>
                                    <span className="bg-emerald-600 text-white px-2 py-0.2 rounded-full text-[10px]">
                                        ${cramSessionTotal} NTD {language === 'zh' ? '已確保' : 'Secured'}
                                    </span>
                                </div>
                            ) : currentCramStatus === 'not_held' ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-100 border border-red-300 text-red-900 font-extrabold text-xs">
                                    <span>✗</span>
                                    <span>{t('att_status_not_held')}</span>
                                </div>
                            ) : (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-100 border border-amber-300 text-amber-900 font-extrabold text-xs">
                                    <span>⏳</span>
                                    <span>{t('att_status_pending')}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2.5 pt-1">
                        <button
                            type="button"
                            disabled={isSavingCramSession}
                            onClick={() => handleRecordCramSession('present')}
                            className={classNames('px-4 py-2 rounded-xl text-xs font-extrabold flex items-center gap-1.5 transition-all shadow-xs cursor-pointer', {
                                'bg-emerald-600 text-white hover:bg-emerald-700 ring-2 ring-emerald-400': currentCramStatus === 'held',
                                'bg-emerald-600 text-white hover:bg-emerald-700': currentCramStatus !== 'held'
                            })}
                        >
                            <span>✓</span>
                            <span>{currentCramStatus === 'held' ? (language === 'zh' ? '✓ 已記錄上課 (更新)' : '✓ Class Recorded (Update)') : t('att_mark_held')}</span>
                        </button>

                        <button
                            type="button"
                            disabled={isSavingCramSession}
                            onClick={() => handleRecordCramSession('absent')}
                            className={classNames('px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5', {
                                'bg-red-600 text-white hover:bg-red-700 ring-2 ring-red-300': currentCramStatus === 'not_held',
                                'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-red-50 hover:text-red-700': currentCramStatus !== 'not_held'
                            })}
                        >
                            <span>✗</span>
                            <span>{t('att_mark_not_held')}</span>
                        </button>

                        {currentCramStatus !== 'pending' && (
                            <button
                                type="button"
                                disabled={isSavingCramSession}
                                onClick={handleResetCramSession}
                                className="px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 text-gray-600 dark:text-gray-400 hover:text-gray-900 text-xs font-bold rounded-xl border border-gray-200 dark:border-gray-700 transition-colors cursor-pointer"
                                title={language === 'zh' ? '重設為待記錄' : 'Reset to Pending'}
                            >
                                <span>↺</span>
                                <span>{t('att_reset')}</span>
                            </button>
                        )}
                    </div>

                    {/* Optional Roster Toggle */}
                    {attendanceData && attendanceData.length > 0 ? (
                        <div className="pt-2 border-t border-purple-100 flex items-center justify-between text-xs">
                            <span className="text-purple-900 font-medium">
                                👥 {language === 'zh' ? `本班已登錄 ${attendanceData.length} 位學生名單 (選填)` : `Roster: ${attendanceData.length} students enrolled (Optional)`}
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowStudentRoster(prev => !prev)}
                                className="text-purple-700 font-bold hover:text-purple-950 cursor-pointer flex items-center gap-1 bg-white dark:bg-gray-800 px-2.5 py-1 rounded-lg border border-purple-200 shadow-2xs"
                            >
                                <span>{showStudentRoster ? t('att_hide_roster') : t('att_view_roster')}</span>
                            </button>
                        </div>
                    ) : (
                        <div className="pt-2 border-t border-purple-100 text-xs text-purple-800 italic">
                            ℹ️ {language === 'zh' ? '本班目前無個別學生名單。只需確認上方是否上課即可確保計入課鐘與月度收入。' : 'No individual student roster enrolled. Simply mark whether class was held above to secure teacher pay.'}
                        </div>
                    )}
                </div>
            )}

            {/* Student Roster Section (Always visible for private classes; collapsible for cram classes) */}
            {(!isCramClass || showStudentRoster) && attendanceData.length > 0 && (
                <>
                    {/* Unified Group Banners (Siblings & Cram Schools) */}
            {multiGroups.length > 0 && (
                <div className="space-y-2.5">
                    {multiGroups.map(group => {
                        const members = group.students;
                        const presentMembers = members.filter(s => s.status === 'present' || s.status === 'late');
                        const pendingMembers = presentMembers.filter(s => s.fee_status !== 'paid');
                        
                        const isCram = group.type === 'cram_school';
                        const preferredMethod = isCram ? 'bank_transfer' : (members[0].payment_method || 'cash');
                        const cramFixedFee = parseFloat(members[0].class_hourly_wage || members[0].class_rate || classInfo?.hourly_wage || classInfo?.rate || 700);
                        const totalPendingAmount = isCram
                            ? (pendingMembers.length > 0 ? cramFixedFee : 0)
                            : pendingMembers.reduce((sum, s) => sum + calculateFee(s), 0);

                        return (
                            <div 
                                key={group.key} 
                                className={classNames('border rounded-xl p-3.5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 shadow-xs', {
                                    'bg-purple-50/80 border-purple-200': isCram,
                                    'bg-indigo-50/80 border-indigo-200': !isCram,
                                })}
                            >
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xl">{isCram ? '🏫' : '👨‍👩‍👧'}</span>
                                        <span className={classNames('font-bold text-sm', isCram ? 'text-purple-950' : 'text-indigo-950')}>
                                            {group.name}
                                        </span>
                                        <span className={classNames('text-xs px-2.5 py-0.5 rounded-full font-medium', isCram ? 'bg-purple-200 text-purple-900' : 'bg-indigo-200 text-indigo-900')}>
                                            {members.length} {isCram ? 'Students Enrolled' : 'Siblings Enrolled'}: {members.map(s => s.student_name).join(', ')}
                                        </span>
                                    </div>
                                    {!isCram && members[0].parent_line_id && (
                                        <p className="text-xs text-green-700 mt-1 flex items-center gap-1 font-medium">
                                            <span>💬 LINE: {members[0].parent_line_id}</span>
                                            <span className="text-gray-400 dark:text-gray-500 font-normal">(Unified notification enabled)</span>
                                        </p>
                                    )}
                                </div>

                                {pendingMembers.length > 0 && (
                                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                                        {preferredMethod === 'bank_transfer' || isCram ? (
                                            <button
                                                type="button"
                                                onClick={() => handleConfirm(
                                                    pendingMembers.map(s => s.student_id),
                                                    totalPendingAmount,
                                                    group.name,
                                                    'bank_transfer'
                                                )}
                                                className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors gap-1.5 cursor-pointer"
                                            >
                                                <span>🏦</span>
                                                <span>Confirm Transfer (${totalPendingAmount} NTD{isCram ? (language === 'zh' ? ' - 全班合約價' : ' - Flat Contract') : ''})</span>
                                            </button>
                                        ) : null}
                                        
                                        <button
                                            type="button"
                                            onClick={() => handleConfirm(
                                                pendingMembers.map(s => s.student_id),
                                                totalPendingAmount,
                                                group.name,
                                                'cash'
                                            )}
                                            className={classNames(
                                                'flex-1 sm:flex-initial inline-flex items-center justify-center px-3.5 py-2 text-xs font-bold rounded-lg shadow-sm transition-colors gap-1.5 cursor-pointer',
                                                preferredMethod === 'cash' 
                                                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                                                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                                            )}
                                        >
                                            <span>💰</span>
                                            <span>{t('att_confirm_cash')} (${totalPendingAmount} NTD)</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Quick Batch Toolbar: One-Click Mark All Present & Multi-Select Actions */}
            <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-gray-50/90 rounded-xl border border-gray-200 dark:border-gray-700 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 font-semibold text-gray-700 dark:text-gray-300 cursor-pointer select-none bg-white dark:bg-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 shadow-2xs">
                        <input 
                            type="checkbox"
                            checked={allSelected}
                            onChange={handleSelectAll}
                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                        />
                        <span>{t('stu_select_all')} ({attendanceData.length})</span>
                    </label>
                    <button
                        type="button"
                        onClick={handleMarkAllPresent}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                        title="One-click: Mark all students as present"
                    >
                        <span>⚡</span>
                        <span>{t('att_mark_all_present')}</span>
                    </button>
                </div>

                {selectedCount > 0 && (
                    <div className="flex items-center gap-1.5 bg-blue-50/90 p-1.5 rounded-lg border border-blue-200 flex-wrap">
                        <span className="font-bold text-blue-900 px-1">{selectedCount} {t('stu_selected')}:</span>
                        <button 
                            type="button"
                            onClick={() => handleBatchSetStatus('present')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-2.5 py-1 rounded text-xs cursor-pointer shadow-xs"
                        >
                            ✓ {t('att_present')}
                        </button>
                        <button 
                            type="button"
                            onClick={() => handleBatchSetStatus('late')}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-2.5 py-1 rounded text-xs cursor-pointer shadow-xs"
                        >
                            ⏱ {t('att_late')}
                        </button>
                        <button 
                            type="button"
                            onClick={() => handleBatchSetStatus('absent')}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold px-2.5 py-1 rounded text-xs cursor-pointer shadow-xs"
                        >
                            ✗ {t('att_absent')}
                        </button>
                        <button 
                            type="button"
                            onClick={() => handleBatchSetStatus('pending')}
                            className="bg-gray-600 hover:bg-gray-700 text-white font-bold px-2.5 py-1 rounded text-xs cursor-pointer shadow-xs"
                        >
                            ⏳ {t('att_status_pending')}
                        </button>
                    </div>
                )}
            </div>

            {/* --- MOBILE CARDS VIEW (Clean & Touch-friendly for < 768px screens) --- */}
            <div className="block md:hidden space-y-3">
                {attendanceData.map((student) => {
                    const fee = calculateFee(student);
                    const isPresent = student.status === 'present' || student.status === 'late';
                    const isPaid = student.fee_status === 'paid';
                    const isCram = student.student_type === 'cram_school' || student.class_type === 'cram_school';
                    const method = student.fee_payment_method || student.payment_method || (isCram ? 'bank_transfer' : 'cash');

                    return (
                        <div 
                            key={`mobile-${student.student_id}`}
                            className={classNames('p-4 rounded-xl border transition-all shadow-xs space-y-3', {
                                'bg-yellow-50/90 border-yellow-300': student.student_id === highlightedStudent,
                                'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700': student.student_id !== highlightedStudent
                            })}
                        >
                            {/* Card Header: Name & Badges */}
                            <div className="flex justify-between items-start">
                                <div className="flex items-start gap-2.5">
                                    <input
                                        type="checkbox"
                                        checked={selectedStudentIds.has(student.student_id)}
                                        onChange={() => handleToggleSelect(student.student_id)}
                                        className="w-4 h-4 mt-1 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                    />
                                    <div>
                                        <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">{student.student_name}</h4>
                                        <div className="flex flex-wrap gap-1.5 mt-1">
                                            {isCram ? (
                                                <span className="bg-purple-100 text-purple-800 text-[11px] px-2 py-0.5 rounded-full font-semibold">
                                                    🏫 {student.cram_school_name || student.class_cram_school_name || 'Cram School'}
                                                </span>
                                            ) : (
                                                <span className="bg-blue-50 text-blue-700 text-[11px] px-2 py-0.5 rounded-full font-medium">
                                                    🏠 Private Tutoring
                                                </span>
                                            )}
                                            {student.is_trial && (
                                                <span className="bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 rounded-full font-bold">Trial</span>
                                            )}
                                            {student.family_name && !isCram && (
                                                <span className="bg-indigo-50 text-indigo-700 text-[11px] px-2 py-0.5 rounded-full font-medium">
                                                    👨‍👩‍👧 {student.family_name}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={classNames('text-sm font-bold block', {
                                        'text-red-600': student.status === 'absent',
                                        'text-gray-900 dark:text-gray-100': student.status !== 'absent'
                                    })}>
                                        {student.status === 'absent' ? '$0 NTD' : `$${fee} NTD`}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 block">
                                        {getRateDescription(student)}
                                    </span>
                                </div>
                            </div>

                            {/* Attendance Quick-Touch Buttons */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                    Attendance Status
                                </label>
                                <div className="grid grid-cols-4 gap-1.5">
                                    {[
                                        { key: 'present', label: t('att_present'), icon: '✓', activeClass: 'bg-emerald-600 text-white font-bold shadow-xs' },
                                        { key: 'late', label: t('att_late'), icon: '⏱', activeClass: 'bg-amber-500 text-white font-bold shadow-xs' },
                                        { key: 'absent', label: t('att_absent'), icon: '✗', activeClass: 'bg-red-600 text-white font-bold shadow-xs' },
                                        { key: 'pending', label: t('att_status_pending'), icon: '⏳', activeClass: 'bg-gray-700 text-white font-bold shadow-xs' },
                                    ].map(opt => {
                                        const isSelected = student.status === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => handleSetStudentStatus(student, opt.key)}
                                                className={classNames(
                                                    'min-h-[42px] py-2 px-1 text-xs rounded-lg border transition-all flex flex-col items-center justify-center cursor-pointer',
                                                    isSelected 
                                                        ? `${opt.activeClass} border-transparent` 
                                                        : 'bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100'
                                                )}
                                            >
                                                <span className="text-sm leading-none mb-0.5">{opt.icon}</span>
                                                <span className="text-[10px] uppercase font-medium">{opt.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {student.status === 'absent' && (
                                    student.existing_makeup_id ? (
                                        <div className="w-full mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between gap-2">
                                            <span className="text-[11px] font-bold text-emerald-800">
                                                ✓ {language === 'zh' ? '已安排補課' : 'Make-up scheduled'}
                                                {student.existing_makeup_date && ` (${new Date(student.existing_makeup_date).toLocaleDateString('zh-TW')})`}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={cancellingMakeupFor === student.student_id}
                                                onClick={() => handleCancelMakeup(student)}
                                                className="text-[11px] font-bold text-red-600 hover:text-red-800 px-2 py-1 bg-white dark:bg-gray-800 rounded border border-red-200 cursor-pointer"
                                            >
                                                🗑️ {language === 'zh' ? '取消' : 'Cancel'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => openMakeupModal(student)}
                                            className="w-full mt-2 py-1.5 px-3 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold rounded-lg border border-blue-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                                        >
                                            <span>{t('att_schedule_makeup')}</span>
                                        </button>
                                    )
                                )}
                                {student.attendance_id && student.status !== 'pending' && (
                                    <button
                                        type="button"
                                        disabled={deletingAttendanceFor === student.student_id}
                                        onClick={() => handleDeleteAttendance(student)}
                                        className="w-full mt-1.5 py-1 px-3 text-gray-400 dark:text-gray-500 hover:text-red-600 text-[11px] font-medium transition-colors flex items-center justify-center gap-1 cursor-pointer"
                                        title={language === 'zh' ? '刪除此筆出席紀錄（修正錯誤）' : 'Delete this attendance record (correct a mistake)'}
                                    >
                                        🗑️ {language === 'zh' ? '刪除紀錄' : 'Delete record'}
                                    </button>
                                )}
                            </div>

                            {/* Payment Status & One-Tap Confirmation Button */}
                            <div className="bg-gray-50/80 p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <div>
                                    <div className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">Billing / Payment Method:</div>
                                    <div className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5 mt-0.5">
                                        {method === 'bank_transfer' ? (
                                            <span className="text-blue-700 font-bold flex items-center gap-1">{t('pay_bank')}</span>
                                        ) : method === 'cash' ? (
                                            <span className="text-amber-800 font-bold flex items-center gap-1">{t('pay_cash')}</span>
                                        ) : (
                                            <span className="text-emerald-700 font-bold flex items-center gap-1">{t('pay_line_pay')}</span>
                                        )}
                                        {isCram && <span className="text-purple-700 text-[11px] font-normal">• {language === 'zh' ? '補習班代結' : 'School Settlement'}</span>}
                                    </div>
                                </div>

                                <div>
                                    {student.payment_schedule === 'per_4_sessions' ? (
                                        student.cycle_needs_confirmation ? (
                                            <button
                                                type="button"
                                                disabled={confirmingCycleFor === student.student_id}
                                                onClick={() => handleConfirmCycle(student, method === 'cash' ? 'cash' : 'bank_transfer')}
                                                className="inline-flex items-center px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-xs font-bold rounded-md shadow-xs transition-colors gap-1 cursor-pointer"
                                            >
                                                <span>🔄 Confirm Cycle</span>
                                                <span>({student.cycle_session_count} sessions, ${student.cycle_total_due})</span>
                                            </button>
                                        ) : (
                                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700">
                                                {student.cycle_session_count || 0}/4 sessions this cycle
                                            </span>
                                        )
                                    ) : isPaid ? (
                                        <span className="inline-flex items-center text-xs font-bold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
                                            ✓ Paid (${student.fee_amount || fee} NTD)
                                        </span>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {student.status === 'absent' ? (
                                                <span className="text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-md border border-red-200 inline-flex items-center gap-1">
                                                    <span>✕</span> {t('att_absent_no_fee')}
                                                </span>
                                            ) : isPresent ? (
                                                method === 'bank_transfer' || isCram ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleConfirm([student.student_id], fee, student.student_name, 'bank_transfer')}
                                                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-md shadow-xs transition-colors gap-1 cursor-pointer"
                                                    >
                                                        <span>🏦 {t('att_confirm_transfer')}</span>
                                                        <span>(${fee})</span>
                                                    </button>
                                                ) : method === 'cash' ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleConfirm([student.student_id], fee, student.student_name, 'cash')}
                                                        className="inline-flex items-center px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-md shadow-xs transition-colors gap-1 cursor-pointer"
                                                    >
                                                        <span>💰 {t('att_confirm_cash')}</span>
                                                        <span>(${fee})</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                                        LINE Pay Pending
                                                    </span>
                                                )
                                            ) : (
                                                <span className="text-xs text-gray-400 dark:text-gray-500 italic">Mark present to confirm</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes Input */}
                            <div>
                                <input
                                    type="text"
                                    value={student.notes || ''}
                                    onChange={(e) => onStatusChange(student.student_id, 'notes', e.target.value)}
                                    className="w-full text-xs border border-gray-200 dark:border-gray-700 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800"
                                    placeholder="Add notes for this student today..."
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- DESKTOP TABLE VIEW (Visible on >= 768px screens) --- */}
            <div className="hidden md:block overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-xl shadow-xs">
                <table className="min-w-full bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50/90">
                        <tr>
                            <th className="py-3 px-3 text-center w-10">
                                <input 
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={handleSelectAll}
                                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                />
                            </th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Student</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Billing Plan</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Fee Rate</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider min-w-[210px]">Attendance</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Payment Status</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">Notes</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {attendanceData.map((student) => {
                            const fee = calculateFee(student);
                            const isPresent = student.status === 'present' || student.status === 'late';
                            const isPaid = student.fee_status === 'paid';
                            const isCram = student.student_type === 'cram_school' || student.class_type === 'cram_school';
                            const method = student.fee_payment_method || student.payment_method || (isCram ? 'bank_transfer' : 'cash');

                            return (
                                <tr 
                                    key={student.student_id} 
                                    className={classNames('hover:bg-gray-50/80 transition-colors', {
                                        'bg-yellow-50/80': student.student_id === highlightedStudent,
                                    })}
                                >
                                    {/* Multi-select checkbox */}
                                    <td className="py-3.5 px-3 text-center w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedStudentIds.has(student.student_id)}
                                            onChange={() => handleToggleSelect(student.student_id)}
                                            className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                        />
                                    </td>

                                    {/* Student name & badges */}
                                    <td className="py-3.5 px-4">
                                        <div className="font-bold text-gray-900 dark:text-gray-100 text-sm">{student.student_name}</div>
                                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                            {isCram ? (
                                                <span className="bg-purple-100 text-purple-800 text-[11px] px-2 py-0.5 rounded-full font-semibold">
                                                    🏫 {student.cram_school_name || student.class_cram_school_name || 'Cram School'}
                                                </span>
                                            ) : (
                                                <span className="bg-blue-50 text-blue-700 text-[11px] px-1.5 py-0.5 rounded font-medium">
                                                    🏠 Private
                                                </span>
                                            )}
                                            {student.is_trial && (
                                                <span className="bg-amber-100 text-amber-800 text-[11px] px-2 py-0.5 rounded font-bold">Trial</span>
                                            )}
                                            {student.family_name && !isCram && (
                                                <span className="bg-indigo-50 text-indigo-700 text-[11px] px-1.5 py-0.5 rounded">
                                                    👨‍👩‍👧 {student.family_name}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Billing Plan */}
                                    <td className="py-3.5 px-4 text-xs">
                                        {isCram ? (
                                            <div>
                                                <span className="inline-block px-2 py-0.5 rounded font-semibold bg-purple-100 text-purple-800">
                                                    🏫 Cram School Plan
                                                </span>
                                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    {student.cram_school_name || student.class_cram_school_name || 'Contract'} {language === 'zh' ? '代結' : 'Settlement'}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className={classNames('inline-block px-2 py-0.5 rounded font-medium', {
                                                    'bg-amber-100 text-amber-800': method === 'cash',
                                                    'bg-green-100 text-green-800': method === 'line_pay',
                                                    'bg-blue-100 text-blue-800': method === 'bank_transfer',
                                                })}>
                                                    {method === 'cash' ? t('pay_cash') : (method === 'line_pay' ? t('pay_line_pay') : t('pay_bank'))}
                                                </span>
                                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    {student.payment_schedule === 'monthly'
                                                        ? (language === 'zh' ? '月結帳單' : 'Monthly Bill')
                                                        : student.payment_schedule === 'per_4_sessions'
                                                            ? (language === 'zh' ? '每 4 堂結算' : 'Every 4 Sessions')
                                                            : (language === 'zh' ? '單堂結算' : 'Weekly (Day of Class)')}
                                                </div>
                                            </div>
                                        )}
                                    </td>

                                    {/* Fee Rate */}
                                    <td className="py-3.5 px-4">
                                        {student.status === 'absent' ? (
                                            <div>
                                                <span className="text-sm font-bold text-red-600">$0 NTD</span>
                                                <span className="text-[11px] text-red-500 block font-normal">{t('att_absent_no_fee')}</span>
                                            </div>
                                        ) : (
                                            <div>
                                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">${fee} NTD</span>
                                                <span className="text-[11px] text-gray-500 dark:text-gray-400 block font-normal">
                                                    {getRateDescription(student)}
                                                </span>
                                            </div>
                                        )}
                                    </td>

                                    {/* Attendance 1-Click Status Pills */}
                                    <td className="py-3.5 px-4 min-w-[210px] whitespace-nowrap">
                                        <div className="inline-flex items-center p-1 bg-gray-100/90 rounded-lg gap-1 border border-gray-200 dark:border-gray-700">
                                            <button
                                                type="button"
                                                onClick={() => handleSetStudentStatus(student, 'present')}
                                                className={classNames('px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer', {
                                                    'bg-emerald-600 text-white shadow-xs': student.status === 'present',
                                                    'text-gray-600 dark:text-gray-400 hover:text-emerald-700 hover:bg-emerald-50': student.status !== 'present',
                                                })}
                                            >
                                                ✓ {t('att_present')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSetStudentStatus(student, 'late')}
                                                className={classNames('px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer', {
                                                    'bg-amber-500 text-white shadow-xs': student.status === 'late',
                                                    'text-gray-600 dark:text-gray-400 hover:text-amber-700 hover:bg-amber-50': student.status !== 'late',
                                                })}
                                            >
                                                ⏱ {t('att_late')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSetStudentStatus(student, 'absent')}
                                                className={classNames('px-2.5 py-1 rounded-md text-xs font-bold transition-all cursor-pointer', {
                                                    'bg-red-600 text-white shadow-xs': student.status === 'absent',
                                                    'text-gray-600 dark:text-gray-400 hover:text-red-700 hover:bg-red-50': student.status !== 'absent',
                                                })}
                                            >
                                                ✗ {t('att_absent')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSetStudentStatus(student, 'pending')}
                                                className={classNames('px-2 py-1 rounded-md text-xs font-bold transition-all cursor-pointer', {
                                                    'bg-gray-600 text-white shadow-xs': student.status === 'pending',
                                                    'text-gray-500 dark:text-gray-400 hover:text-gray-800 hover:bg-gray-200': student.status !== 'pending',
                                                })}
                                                title={t('att_status_pending')}
                                            >
                                                ⏳
                                            </button>
                                        </div>
                                        {student.status === 'absent' && (
                                            <div className="mt-1.5">
                                                {student.existing_makeup_id ? (
                                                    <div className="inline-flex items-center gap-1.5">
                                                        <span className="text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-md">
                                                            ✓ {language === 'zh' ? '已安排補課' : 'Make-up scheduled'}
                                                            {student.existing_makeup_date && ` (${new Date(student.existing_makeup_date).toLocaleDateString('zh-TW')})`}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            disabled={cancellingMakeupFor === student.student_id}
                                                            onClick={() => handleCancelMakeup(student)}
                                                            className="text-[11px] font-bold text-red-600 hover:text-red-800 px-2 py-1 bg-white dark:bg-gray-800 rounded-md border border-red-200 cursor-pointer"
                                                        >
                                                            🗑️ {language === 'zh' ? '取消' : 'Cancel'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => openMakeupModal(student)}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-[11px] font-bold rounded-md border border-blue-200 transition-colors cursor-pointer shadow-2xs"
                                                        title="Schedule one-off make-up session"
                                                    >
                                                        <span>{t('att_schedule_makeup')}</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        {student.attendance_id && student.status !== 'pending' && (
                                            <button
                                                type="button"
                                                disabled={deletingAttendanceFor === student.student_id}
                                                onClick={() => handleDeleteAttendance(student)}
                                                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 dark:text-gray-500 hover:text-red-600 transition-colors cursor-pointer"
                                                title={language === 'zh' ? '刪除此筆出席紀錄（修正錯誤）' : 'Delete this attendance record (correct a mistake)'}
                                            >
                                                🗑️ {language === 'zh' ? '刪除紀錄' : 'Delete record'}
                                            </button>
                                        )}
                                    </td>

                                    {/* Payment Action & Status */}
                                    <td className="py-3.5 px-4">
                                        {student.payment_schedule === 'per_4_sessions' ? (
                                            student.cycle_needs_confirmation ? (
                                                <button
                                                    type="button"
                                                    disabled={confirmingCycleFor === student.student_id}
                                                    onClick={() => handleConfirmCycle(student, method === 'cash' ? 'cash' : 'bank_transfer')}
                                                    className="inline-flex items-center px-3 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-xs font-bold rounded-lg shadow-xs transition-colors gap-1 cursor-pointer"
                                                >
                                                    <span>🔄 Confirm Cycle</span>
                                                    <span>({student.cycle_session_count} sessions, ${student.cycle_total_due})</span>
                                                </button>
                                            ) : (
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700">
                                                    {student.cycle_session_count || 0}/4 sessions this cycle
                                                </span>
                                            )
                                        ) : isPaid ? (
                                            <span className="inline-flex items-center text-xs font-bold text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
                                                ✓ Paid (${student.fee_amount || fee} NTD)
                                            </span>
                                        ) : (
                                            <>
                                                {student.status === 'absent' ? (
                                                    <span className="text-xs font-semibold text-red-600 bg-red-50 px-2.5 py-1 rounded-md border border-red-200 inline-flex items-center gap-1">
                                                        <span>✕</span> {t('att_absent_no_fee')}
                                                    </span>
                                                ) : isPresent ? (
                                                    method === 'bank_transfer' || isCram ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleConfirm([student.student_id], fee, student.student_name, 'bank_transfer')}
                                                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs transition-colors gap-1 cursor-pointer"
                                                            title="Admin/Teacher verifies bank deposit and confirms"
                                                        >
                                                            <span>🏦 {t('att_confirm_transfer')}</span>
                                                            <span>(${fee})</span>
                                                        </button>
                                                    ) : method === 'cash' ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleConfirm([student.student_id], fee, student.student_name, 'cash')}
                                                            className="inline-flex items-center px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs transition-colors gap-1 cursor-pointer"
                                                        >
                                                            <span>💰 {t('att_confirm_cash')}</span>
                                                            <span>(${fee})</span>
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                                            LINE Pay Pending
                                                        </span>
                                                    )
                                                ) : (
                                                    <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                                                        {method === 'bank_transfer' ? 'Pending Transfer' : (method === 'cash' ? 'Awaiting Cash' : 'Pending')}
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </td>

                                    {/* Notes input */}
                                    <td className="py-3.5 px-4">
                                        <input
                                            type="text"
                                            value={student.notes || ''}
                                            onChange={(e) => onStatusChange(student.student_id, 'notes', e.target.value)}
                                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-1.5 w-full text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Add note..."
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            </>
            )}

            {/* --- MODAL 1: Absent Student Make-Up Prompt --- */}
            {absentPromptStudent && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-100 dark:border-gray-700 animate-in zoom-in-95">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2.5">
                                <span className="text-3xl">🗓️</span>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{t('att_schedule_makeup_prompt_title')}</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{language === 'zh' ? '學生已記錄請假' : 'Student marked absent'}</p>
                                </div>
                            </div>
                            <button 
                                type="button"
                                onClick={() => setAbsentPromptStudent(null)} 
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 text-xl font-bold cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="bg-amber-50 p-3.5 rounded-xl border border-amber-200 text-sm text-gray-800 dark:text-gray-200 space-y-1">
                            <p>
                                {language === 'zh' ? (
                                    <>學生 <strong>{absentPromptStudent.student_name}</strong> 剛被標記為<strong>請假</strong>。</>
                                ) : (
                                    <>Student <strong>{absentPromptStudent.student_name}</strong> was marked <strong>Absent</strong>.</>
                                )}
                            </p>
                            <p className="text-xs text-amber-900 font-medium">
                                {language === 'zh' ? (
                                    <>是否要為該學生立即安排<strong>單次補課</strong>？</>
                                ) : (
                                    <>Would you like to schedule a <strong>one-off make-up class</strong> for this student?</>
                                )}
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
                            <button
                                type="button"
                                onClick={() => {
                                    const st = absentPromptStudent;
                                    setAbsentPromptStudent(null);
                                    openMakeupModal(st);
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-4 rounded-xl text-sm shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                            >
                                <span>{t('att_yes_schedule')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setAbsentPromptStudent(null)}
                                className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold rounded-xl text-sm transition-colors cursor-pointer"
                            >
                                {t('att_skip_makeup')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MODAL 2: One-Off Make-Up Class Scheduling Modal --- */}
            {makeupModalStudent && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4 overflow-y-auto animate-in fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4 border border-gray-100 dark:border-gray-700 my-8 animate-in zoom-in-95">
                        <div className="flex items-start justify-between border-b pb-3">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <span>📅</span> {t('att_schedule_makeup_modal_title')}
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {language === 'zh' ? '學生' : 'Student'}：<strong className="text-gray-800 dark:text-gray-200">{makeupModalStudent.student_name}</strong> • {language === 'zh' ? '原班級' : 'Original Class'}：{classInfo?.name || 'Class'}
                                </p>
                            </div>
                            <button 
                                type="button"
                                onClick={() => setMakeupModalStudent(null)}
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 text-xl font-bold cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleScheduleMakeupSubmit} className="space-y-3.5 text-xs">
                            {/* Make-up Date */}
                            <div>
                                <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                    {t('att_makeup_date')} <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    type="date"
                                    required
                                    value={makeupForm.date}
                                    onChange={e => setMakeupForm(prev => ({ ...prev, date: e.target.value }))}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                />
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">{t('att_makeup_notice')}</p>
                            </div>

                            {/* Time Range */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        {t('att_start_time')} <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="time"
                                        required
                                        value={makeupForm.startTime}
                                        onChange={e => setMakeupForm(prev => ({ ...prev, startTime: e.target.value }))}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                    />
                                </div>
                                <div>
                                    <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        {t('att_end_time')} <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="time"
                                        required
                                        value={makeupForm.endTime}
                                        onChange={e => setMakeupForm(prev => ({ ...prev, endTime: e.target.value }))}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                    />
                                </div>
                            </div>

                            {/* Format & Teacher */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        {t('att_class_format')}
                                    </label>
                                    <select
                                        value={makeupForm.format}
                                        onChange={e => setMakeupForm(prev => ({ ...prev, format: e.target.value }))}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                    >
                                        <option value="in_person">{t('att_format_in_person')}</option>
                                        <option value="online">{t('att_format_online')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                        {t('att_assign_teacher')}
                                    </label>
                                    <select
                                        value={makeupForm.teacherId}
                                        onChange={e => setMakeupForm(prev => ({ ...prev, teacherId: e.target.value }))}
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                    >
                                        <option value="">{t('att_use_class_teacher')}</option>
                                        {teachersList.map(t => (
                                            <option key={t.id} value={t.id}>{t.username} ({t.role})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block font-bold text-gray-700 dark:text-gray-300 mb-1">
                                    {t('att_makeup_notes')}
                                </label>
                                <input 
                                    type="text"
                                    value={makeupForm.notes}
                                    onChange={e => setMakeupForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder={t('att_makeup_notes_placeholder')}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 font-medium"
                                />
                            </div>

                            {/* Submit buttons */}
                            <div className="flex justify-end gap-2 pt-3 border-t">
                                <button
                                    type="button"
                                    onClick={() => setMakeupModalStudent(null)}
                                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-300 font-semibold rounded-lg text-xs transition-colors cursor-pointer"
                                >
                                    {t('common_cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={isScheduling}
                                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-lg text-xs shadow-sm transition-colors cursor-pointer flex items-center gap-1.5"
                                >
                                    {isScheduling ? (
                                        <span>{language === 'zh' ? '建立中...' : 'Scheduling...'}</span>
                                    ) : (
                                        <span>✓ {t('att_confirm_schedule')}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}